/** 
 * @module PlanVerificationPageColumns
 * @description Plan Verification Page component for displaying plan details in the PlanVerificationPage table.
 */
import { convertLocaltoUTCFormat } from "../../../services/DateFormatUtility";
import FlagIcon from "@mui/icons-material/Flag";

export const PlanVerificationPageColumns = [
  // Plan type
  {
    accessorKey: "Plan",
    accessorFn: (row) => row.Plan || "",
    size: 50,
    enableHiding: false,
    header: "Plan",
    Cell: ({ cell }) => {
      const plan = cell.getValue();
      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <span style={{ display: "flex", alignItems: "center" }}>
            <span>{plan}</span>
          </span>
        </div>
      );
    },
  },

  // IMO
  {
    accessorKey: "ImoNumber",
    accessorFn: (row) => {
      return row.ImoNumber || "";
    },
    enableHiding: false,
    size: 45,
    header: "IMO Number",
  },

  // Vessel name
  {
    accessorKey: "VesselName",
    accessorFn: (row) => {
      return row.VesselName || "";
    },
    header: "Vessel Name",
  },

  // WCN
  {
    accessorKey: "WcnNumber",
    accessorFn: (row) => {
      return row.WcnNumber || "";
    },
    enableHiding: false,
    size: 40,
    header: "WCN",
  },

  // Company
  {
    accessorKey: "CompanyName",
    accessorFn: (row) => {
      return row.CompanyName || "";
    },
    enableHiding: false,
    header: "Company",
    muiTableBodyCellProps: ({ row }) => ({
      sx: {
        minWidth: "300px",
        whiteSpace: "pre-line",
        wordWrap: "break-word",
        overflowWrap: "break-word",
        fontWeight: row.original.IsParentReport ? "bold" : "null",
      },
    }),
  },

  // IMO Vessel Type
  {
    accessorKey: "ImoVesselTypeName",
    accessorFn: (row) => {
      return row.ImoVesselTypeName || "";
    },
    header: "IMO Vessel Type",
  },

  // MRV Vessel Type
  {
    accessorKey: "MrvVesselTypeName",
    accessorFn: (row) => {
      return row.MrvVesselTypeName || "";
    },
    size: 50,
    header: "MRV Vessel Type",
  },

  // Flag
  {
    accessorKey: "Flag",
    accessorFn: (row) => {
      return row.Flag || "";
    },
    size: 40,
    header: "Flag",
  },

  // Submission Date
  {
    accessorKey: "SubmissionDate",
    accessorFn: (row) =>
      row.SubmissionDate ? (
        <span>{convertLocaltoUTCFormat(new Date(row.SubmissionDate))}</span>
      ) : (
        ""
      ),
    size: 50,
    header: "Submission Date",
    enableColumnFilter: false,
  },

  // Verified Date
  {
    accessorKey: "VerifiedDate",
    accessorFn: (row) =>
      row.VerifiedDate ? (
        <span>{convertLocaltoUTCFormat(new Date(row.VerifiedDate))}</span>
      ) : (
        ""
      ),
    size: 50,
    header: "Verified Date",
    enableColumnFilter: false,
  },

  // Modified Date
  {
    accessorKey: "ModifiedDate",
    id: "ModifiedDate",
    header: "Modified Date",
    accessorFn: (row) =>
      row.LastModified ? (
        <span>{convertLocaltoUTCFormat(new Date(row.LastModified))}</span>
      ) : (
        ""
      ),
    size: 50,
    enableColumnFilter: false,
  },

  // Corrections (flag icon if corrections unresolved)
  {
    accessorKey: "Correction",
    accessorFn: (row) => {
      const corrections = row?.Corrections ?? row?.Correction ?? [];
      const items = Array.isArray(corrections)
        ? corrections
        : Array.isArray(corrections?.Messages)
        ? corrections.Messages
        : [];
      return items.filter((c) => (c?.Status ?? "").toLowerCase() !== "resolved").length;
    },
    header: "Correction",
    size: 40,
    Cell: ({ row }) => {
      const corrections = row.original?.Corrections ?? row.original?.Correction ?? [];
      const items = Array.isArray(corrections)
        ? corrections
        : Array.isArray(corrections?.Messages)
        ? corrections.Messages
        : [];

      const unresolvedCount = items.reduce((acc, c) => {
        const status = (c?.Status ?? "").toLowerCase();
        return acc + (status !== "resolved" ? 1 : 0);
      }, 0);

      return unresolvedCount > 0 ? (
        <span title={`${unresolvedCount} unresolved correction${unresolvedCount > 1 ? "s" : ""}`}>
          <FlagIcon color="error" fontSize="small" />
        </span>
      ) : null;
    },
  },

  // Status
  {
    accessorKey: "VerificationStatus",
    accessorFn: (row) => {
      return row.VerificationStatus || "";
    },
    size: 50,
    header: "Status",
  },

  // Reviewer
  {
    accessorKey: "Reviewer",
    accessorFn: (row) => {
      return row.Reviewer || "";
    },
    size: 65,
    header: "Reviewer",
  },

  // Verifier
  {
    accessorKey: "Verifier",
    accessorFn: (row) => {
      return row.Verifier || "";
    },
    size: 65,
    header: "Verifier",
  },
];